/* ================================================================
   ZZ MARTS - SIDE CART BEHAVIOUR
   ----------------------------------------------------------------
   Three jobs:

   1. OPEN THE DRAWER ON ADD TO CART.
      Horizon ships a drawer (cart_type: drawer) but only the header
      cart icon opens it, bound declaratively as
      on:click="#cart-drawer/toggle". Adding from a product card or a
      product page bumped the badge and did nothing else, so the
      customer got no confirmation that anything had happened.

      This hooks the NETWORK layer rather than Horizon's internal
      events, because those event names change between theme versions
      and fail silently when they do. Any successful POST to /cart/add
      opens #cart-drawer. That covers product pages, product cards,
      quick add, and anything an app adds later, at every breakpoint.

      Dynamic checkout ("Buy it now") does NOT post to /cart/add - it
      builds its own checkout - so it still goes straight to checkout,
      untouched by this file.

   2. FLAG THE LINE THAT WAS JUST ADDED.
      /cart/add.js returns the added line's `key`, and Horizon stamps
      the same value on the row as data-key. Matching the two lets the
      drawer point at the row the customer just created, which is the
      one question they actually have when the panel appears. The
      class is all this file does; the ring animation lives in
      zz-cart.css so it can be removed without touching behaviour.

   3. FREE DELIVERY PROGRESS, IN THE DRAWER.
      The store promises free delivery over Rs 5,000 in the header, the
      policies, the About page and 40-odd blog posts. The drawer is one
      of the two places the customer can act on it.

      The bar is driven with transform: scaleX(), NOT width. Animating
      width forces layout on every frame; scaleX is compositor-only.

      >>> THE /cart PAGE VERSION LIVES IN zz-cart-trust.js <<<
      Same bar, different surface. It is not duplicated here because
      the two surfaces re-render on completely different triggers and
      merging them made both harder to follow. What they MUST share is
      the threshold, so it is published on window below.

   ================================================================
   THE THRESHOLD IS PUBLISHED, NOT COPIED
   ================================================================
   FREE_SHIP is exported as window.ZZ_FREE_SHIP so zz-cart-trust.js
   reads the same number rather than declaring its own. This theme has
   been bitten repeatedly by one value written in several files - the
   free delivery amount alone is still duplicated in prose across the
   announcement bar, the product template, the shop pages and two
   collection descriptions. At least the two progress bars cannot
   disagree with each other.

   If the amount ever changes it must ALSO change in:
     Settings > Shipping        the only one that charges the customer
     snippets/zz-buybox.liquid  free_ship_threshold + the delivery list
     sections/header-group.json announcement bar
     templates/product.json     delivery FAQ answer
     sections/zz-store.liquid   closing CTA on the shop pages
     the Components and Terminal Blocks collection descriptions

   VERIFIED 29 Aug 2026 against Settings > Shipping: domestic orders
   under Rs 5,000 pay Rs 200, Rs 5,000 and over ship free. The site's
   promise and the actual rate agree.
   ================================================================ */
(function () {
  'use strict';

  var FREE_SHIP = 500000;   // Rs 5,000 in the smallest currency unit
  var OPEN_DELAY = 220;     // let Horizon re-render the drawer first
  var FLAG_WINDOW = 4000;   // how long an add stays "recent" for the ring
  var lastOpen = 0;
  var pendingKey = null;
  var pendingUntil = 0;

  // single source of truth for the /cart page bar - see header
  window.ZZ_FREE_SHIP = FREE_SHIP;

  /* ---------- 1. open on add ---------- */

  function isAddRequest(url) {
    try { return /\/cart\/add(\.js)?(\?|$)/.test(String(url)); }
    catch (e) { return false; }
  }

  function rememberKey(res) {
    if (!res || !res.ok || typeof res.clone !== 'function') return;
    res.clone().json().then(function (data) {
      if (!data) return;
      var key = data.key || (data.items && data.items[0] && data.items[0].key);
      if (!key) return;
      pendingKey = key;
      pendingUntil = Date.now() + FLAG_WINDOW;
    }).catch(function () {});
  }

  function openDrawer() {
    var now = Date.now();
    if (now - lastOpen < 500) return;     // one open per burst of requests
    var drawer = document.getElementById('cart-drawer');
    if (!drawer || typeof drawer.open !== 'function') return;
    var dialog = drawer.querySelector('dialog');
    if (dialog && dialog.open) { syncCart(); return; }
    lastOpen = now;
    try { drawer.open(); } catch (e) { return; }
    syncCart();
  }

  var nativeFetch = window.fetch;
  if (typeof nativeFetch === 'function') {
    window.fetch = function (input) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var promise = nativeFetch.apply(this, arguments);
      if (isAddRequest(url)) {
        promise.then(function (res) {
          if (!res || !res.ok) return;
          rememberKey(res);
          setTimeout(openDrawer, OPEN_DELAY);
        }).catch(function () {});
      }
      return promise;
    };
  }

  // Belt and braces: a few Shopify apps still add to cart over XHR.
  var xhrOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    this.__zzIsAdd = isAddRequest(url);
    return xhrOpen.apply(this, arguments);
  };
  var xhrSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.send = function () {
    if (this.__zzIsAdd) {
      this.addEventListener('load', function () {
        if (this.status >= 200 && this.status < 300) setTimeout(openDrawer, OPEN_DELAY);
      });
    }
    return xhrSend.apply(this, arguments);
  };

  /* ---------- 2. flag the line that was just added ---------- */

  function flagAdded() {
    if (!pendingKey || Date.now() > pendingUntil) return;
    // data-key contains a colon; a quoted attribute selector handles it
    // without needing CSS.escape.
    var row = document.querySelector('#cart-drawer [data-key="' + pendingKey + '"]');
    if (!row) return;
    row.classList.remove('zzcart--added');
    void row.offsetWidth;              // force a reflow so the animation replays
    row.classList.add('zzcart--added');
    pendingKey = null;
  }

  /* ---------- state class, so CSS can react ---------- */

  function watchOpenState() {
    var drawer = document.getElementById('cart-drawer');
    var dialog = drawer && drawer.querySelector('dialog');
    if (!dialog) return;
    var apply = function () {
      document.documentElement.classList.toggle('zz-cart-open', dialog.open);
      if (dialog.open) { syncCart(); flagAdded(); }
    };
    new MutationObserver(apply).observe(dialog, { attributes: true, attributeFilter: ['open'] });
    apply();
  }

  /* ---------- 3. free delivery progress ---------- */

  function money(amount) {
    return 'Rs ' + Math.ceil(amount / 100).toLocaleString('en-US');
  }

  function render(cart) {
    var summary = document.querySelector('#cart-drawer .cart-drawer__summary');
    if (!summary) return;
    var bar = summary.querySelector('.zzcart__ship');

    if (!cart || !cart.item_count) { if (bar) bar.remove(); return; }

    if (!bar) {
      bar = document.createElement('div');
      bar.className = 'zzcart__ship';
      bar.innerHTML =
        '<p class="zzcart__ship-note"></p>' +
        '<div class="zzcart__ship-track"><span class="zzcart__ship-fill"></span></div>';
      summary.insertBefore(bar, summary.firstChild);
    }

    var total = cart.total_price || 0;
    var note = bar.querySelector('.zzcart__ship-note');
    var fill = bar.querySelector('.zzcart__ship-fill');
    var unlocked = total >= FREE_SHIP;

    bar.classList.toggle('is-unlocked', unlocked);
    note.textContent = unlocked
      ? 'Free delivery applied to this order.'
      : 'Add ' + money(FREE_SHIP - total) + ' more for free delivery.';

    // scaleX, not width - see the header note.
    fill.style.transform = 'scaleX(' + Math.min(1, total / FREE_SHIP).toFixed(4) + ')';
  }

  var syncing = false;
  function syncCart() {
    if (syncing) return;
    syncing = true;
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(render)
      .catch(function () {})
      .then(function () { syncing = false; });
  }

  /* Horizon replaces the whole summary on every quantity change, which
     wipes the injected bar. Re-render when that happens - but ignore
     mutations we caused ourselves, or this observer feeds itself. */
  function watchCartContents() {
    var drawer = document.getElementById('cart-drawer');
    if (!drawer) return;
    var timer = null;
    new MutationObserver(function (records) {
      var ours = records.every(function (r) {
        return r.target.closest && r.target.closest('.zzcart__ship');
      });
      if (ours) return;
      clearTimeout(timer);
      timer = setTimeout(function () { syncCart(); flagAdded(); }, 150);
    }).observe(drawer, { childList: true, subtree: true });
  }

  function init() {
    watchOpenState();
    watchCartContents();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
